import type { CaseImageVariantListItem } from '@messenger/types'
import {
  caseImageAliasPaths,
  caseImageUrlPath,
  itemFollowsCaseImage,
} from '@messenger/utils'
import type { CaseImageRecord } from '@/api/caseImages'

/**
 * The parts of a canvas item this swap reads. Kept structural rather than
 * importing the editor's item type so the rule can be tested with plain
 * objects.
 */
export type SwappableImageItem = {
  id?: string
  type?: string
  image?: {
    url?: string
    editedUrl?: string
    aiVariantId?: string
  }
}

/**
 * Every URL that has stood for one case photo: the file as uploaded, its
 * MIO-enhanced copy, the URL the server currently prefers, and the output of
 * every AI alteration generated from it.
 *
 * A canvas item holds whichever of those was current when the photo was
 * placed, so all of them have to count as "this photo" when deciding which
 * items an accepted enhancement replaces, or which a revert puts back on the
 * original. Without the variant URLs, a photo
 * enhanced twice would stop matching after the first accept, because the item
 * would be pointing at the first enhancement's output and the image record
 * only names its original and MIO files.
 *
 * The rule is the server's (`caseImageAliasPaths` in `@messenger/utils`),
 * which moves every saved page of the case the same way.
 */
export const caseImageUrlAliases = (
  image: CaseImageRecord,
  variants: CaseImageVariantListItem[] = [],
): Set<string> =>
  caseImageAliasPaths(
    image,
    variants.map((variant) => variant.url),
  )

type ItemsToSwapArgs = {
  /** Every item on the canvas, in any order. */
  items: SwappableImageItem[]
  /** Output of `caseImageUrlAliases` for the photo that just changed. */
  aliases: Set<string>
  /**
   * The URL the photo should show now (the image record's new `displayUrl`):
   * the enhancement after an accept or an undone revert, the original after
   * a revert.
   */
  nextUrl: string
}

/**
 * Ids of the canvas items that are showing this photo and should follow it to
 * the file it shows now: the accepted enhancement's after an accept or an
 * undone revert, the original's after a revert.
 *
 * Four kinds of item are deliberately left alone (`itemFollowsCaseImage`
 * in `@messenger/utils`, the rule the server applies to every saved page):
 *
 * - Anything that is not an image item.
 * - An item still holding the template placeholder. Those resolve through
 *   `filledImagesSelector`, which is derived from the reloaded image list, so
 *   they pick up the enhancement on their own; no case photo's file is a
 *   placeholder URL, so they never match.
 * - An item already pointing at `nextUrl`, so a re-accept is a no-op and the
 *   canvas is not re-rendered for nothing.
 * - An item with an `editedUrl` (a Pintura render, or an AI cutout, which
 *   also carries its `aiVariantId`). That render was made from the older
 *   file, and it is what `determineImageUrl` shows, so swapping the URL
 *   underneath it would change nothing on screen while quietly claiming the
 *   item shows the enhancement. Their crop and filters are theirs to keep;
 *   re-running an edit against the enhanced file is a separate, deliberate
 *   action.
 */
export const itemsToSwapToEnhancedImage = ({
  items,
  aliases,
  nextUrl,
}: ItemsToSwapArgs): string[] => {
  if (!nextUrl || aliases.size === 0) return []
  const nextPath = caseImageUrlPath(nextUrl)

  return items
    .filter(
      (item): item is SwappableImageItem & { id: string } =>
        Boolean(item?.id) && itemFollowsCaseImage(item, aliases, nextPath),
    )
    .map((item) => item.id)
}
